import React, { useState, useEffect, useCallback } from 'react';
import { X, ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import Logo2 from '../logos/Logo2';

const GROUP_SUGGESTIONS = [
  'General Expenses',
  'Fixed Expenses',
  'Savings & Investments',
  'Food & Entertainment',
  'Transport',
  'Personal Care',
  'Healthcare',
  'Travel',
];

const GROUP_TO_CATEGORIES = {
  'General Expenses':      ['Groceries', 'Shopping', 'Clothing', 'Personal Care'],
  'Fixed Expenses':        ['Rent / Mortgage', 'Utilities', 'Insurance', 'Subscriptions', 'Phone Bill', 'Software', 'Tax'],
  'Savings & Investments': ['Savings', 'Emergency Fund', 'Investments', 'Education'],
  'Food & Entertainment':  ['Groceries', 'Dining Out', 'Cinema', 'Hobbies'],
  'Transport':             ['Public Transport', 'Fuel', 'Car Insurance', 'Parking', 'Taxi / Uber'],
  'Personal Care':         ['Haircut', 'Gym & Fitness', 'Toiletries'],
  'Healthcare':            ['Doctor', 'Dentist', 'Prescriptions', 'Therapy'],
  'Travel':                ['Flights', 'Hotels', 'Holiday Activities', 'Travel Insurance'],
};

// Categories pre-selected in step 3 — kept under 50% per group
const PRE_SELECTED = new Set([
  'Groceries', 'Shopping',                         // General Expenses: 2/4
  'Rent / Mortgage', 'Utilities', 'Phone Bill',    // Fixed Expenses: 3/7
  'Savings', 'Emergency Fund',                     // Savings & Investments: 2/4
  'Dining Out',                                    // Food & Entertainment: 1/4 (Groceries via General)
  'Public Transport', 'Fuel',                      // Transport: 2/5
  'Gym & Fitness',                                 // Personal Care: 1/3
  'Doctor', 'Dentist',                             // Healthcare: 2/4
  'Flights', 'Hotels',                             // Travel: 2/4
]);

const CATEGORY_GROUP_PRIORITY = Object.entries(GROUP_TO_CATEGORIES).reduce((acc, [group, cats]) => {
  cats.forEach(cat => {
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(group);
  });
  return acc;
}, {});

const getSuggestedCategories = (selectedGroups) => {
  if (selectedGroups.size === 0) return [];
  const seen = new Set();
  const result = [];
  selectedGroups.forEach(group => {
    (GROUP_TO_CATEGORIES[group] || []).forEach(cat => {
      if (!seen.has(cat)) { seen.add(cat); result.push(cat); }
    });
  });
  return result;
};

const getAutoSelectedCategories = (selectedGroups) => {
  const all = getSuggestedCategories(selectedGroups);
  return new Set(all.filter(cat => PRE_SELECTED.has(cat)));
};

const findBestGroup = (cat, selectedGroupsSet, groupList) => {
  const priorities = CATEGORY_GROUP_PRIORITY[cat] || [];
  for (const g of priorities) {
    if (selectedGroupsSet.has(g)) return g;
  }
  return groupList[0];
};

const buildTree = (selectedGroups, selectedCategories) => {
  const groupList = Array.from(selectedGroups);
  if (groupList.length === 0) return null;

  const groupCats = {};
  groupList.forEach(g => (groupCats[g] = []));

  Array.from(selectedCategories).forEach(cat => {
    const group = findBestGroup(cat, selectedGroups, groupList);
    groupCats[group].push(cat);
  });

  const lines = [];
  groupList.forEach((group, gi) => {
    const isLastGroup = gi === groupList.length - 1;
    const groupPrefix = isLastGroup ? '└─' : '├─';
    const childIndent = isLastGroup ? '   ' : '│  ';

    lines.push({ text: `${groupPrefix} ${group}`, isGroup: true });

    const cats = groupCats[group];
    if (cats.length > 0) {
      cats.forEach((cat, ci) => {
        const isLastCat = ci === cats.length - 1;
        lines.push({
          text: `${childIndent}${isLastCat ? '└─' : '├─'} ${cat}`,
          isGroup: false,
        });
      });
    } else {
      lines.push({ text: `${childIndent}└─ ...`, isGroup: false, isPlaceholder: true });
    }
  });

  return lines;
};

const ProgressBar = ({ step }) => {
  const progress = step === 2 ? 33 : step === 3 ? 66 : step === 4 ? 100 : 0;
  if (progress === 0) return null;
  return (
    <div className="h-1 bg-background/30 overflow-hidden">
      <div
        className="h-full transition-all duration-500 ease-out bg-primary"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

const OnboardingModal = ({ isOpen, onClose, isDemo, onComplete }) => {
  const [step, setStep] = useState(1);
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [income, setIncome] = useState('');

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setSelectedGroups(new Set());
      setSelectedCategories(new Set());
      setIncome('');
    }
  }, [isOpen]);

  const handleClose = useCallback(() => onClose(), [onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const toggleGroup = (name) => {
    setSelectedGroups(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleCategory = (name) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleNextToCategories = () => {
    setSelectedCategories(getAutoSelectedCategories(selectedGroups));
    setStep(3);
  };

  const handleFinish = () => {
    if (!isDemo && onComplete) {
      onComplete(Array.from(selectedGroups), Array.from(selectedCategories), income);
    } else {
      onClose();
    }
  };

  const treeLines = buildTree(selectedGroups, step >= 3 ? selectedCategories : new Set());
  const suggestedCategories = getSuggestedCategories(selectedGroups);

  const stepLabel = step === 2 ? '1 of 3' : step === 3 ? '2 of 3' : step === 4 ? '3 of 3' : '';

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4"
      onClick={onClose}
    >
      {/* ── Step 1: Welcome ── */}
      {step === 1 && (
        <div
          className="bg-background border border-border w-full sm:w-[480px] sm:max-w-[95vw] flex flex-col"
          style={{ minHeight: 400 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex-1 flex flex-col items-start justify-center px-6 py-8 sm:px-10 sm:py-12 gap-6">
            <div className="flex items-center gap-3">
              <Logo2 className="h-8 w-8" />
              <span className="text-xl font-semibold text-primary tracking-tight">DarBudget</span>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-foreground mb-3 leading-tight">Welcome.</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Let's set up your budget structure in a couple of quick steps.
              </p>
            </div>
            <div className="flex flex-col gap-2 w-full">
              <button
                onClick={() => setStep(2)}
                className="w-full h-11 bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Get Started →
              </button>
              <button
                onClick={onClose}
                className="w-full h-9 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip setup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Steps 2 & 3: Two-column ── */}
      {(step === 2 || step === 3) && (
        <div
          className="bg-background border border-border w-full h-[85vh] sm:h-auto sm:w-[700px] sm:max-w-[95vw] flex flex-col"
          style={{ minHeight: 480 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Progress bar */}
          <ProgressBar step={step} />

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-border">
            <span className="text-xs font-mono text-muted-foreground">Step {stepLabel}</span>
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-1 min-h-0">
            {/* Left: chips */}
            <div className="flex-1 px-3 py-3 sm:px-6 sm:py-5 border-r border-border flex flex-col gap-3 sm:gap-4 overflow-y-auto">
              <div>
                <h3 className="text-sm sm:text-base font-semibold text-foreground mb-1">
                  {step === 2 ? 'Choose your group categories' : 'Choose categories'}
                </h3>
                <p className="text-[11px] sm:text-xs text-muted-foreground">
                  {step === 2
                    ? 'Groups help organise your categories. Select the ones that apply to you.'
                    : 'Relevant categories for your groups. You can change these later.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {(step === 2 ? GROUP_SUGGESTIONS : suggestedCategories).map(name => {
                  const active = step === 2 ? selectedGroups.has(name) : selectedCategories.has(name);
                  return (
                    <button
                      key={name}
                      onClick={() => step === 2 ? toggleGroup(name) : toggleCategory(name)}
                      className={cn(
                        'px-2.5 py-1.5 sm:px-3 sm:py-2 text-[11px] sm:text-xs border transition-colors',
                        active
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'border-border text-foreground hover:bg-accent'
                      )}
                    >
                      <span className="opacity-50 mr-1">{active ? '+' : '−'}</span>{name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: tree preview */}
            <div className="w-[200px] sm:w-[220px] shrink-0 px-3 py-3 sm:px-5 sm:py-5 flex flex-col gap-2 sm:gap-3 overflow-y-auto overflow-x-hidden">
              <span className="text-[10px] sm:text-xs font-medium text-muted-foreground uppercase tracking-wider">Preview</span>
              <div className="font-mono text-[10px] sm:text-xs leading-relaxed">
                {treeLines === null ? (
                  <span className="text-muted-foreground/40 italic text-[10px] sm:text-xs">
                    select groups to preview
                  </span>
                ) : (
                  treeLines.map((line, i) => (
                    <div
                      key={i}
                      className={cn(
                        'whitespace-pre leading-4 sm:leading-5 truncate',
                        line.isPlaceholder
                          ? 'text-muted-foreground/30'
                          : line.isGroup
                          ? 'text-foreground font-medium'
                          : 'text-muted-foreground'
                      )}
                    >
                      {line.text}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-t border-border">
            <button
              onClick={() => setStep(step - 1)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setStep(4)}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors min-w-[64px] px-3 py-2"
              >
                Skip
              </button>
              <button
                onClick={step === 2 ? handleNextToCategories : () => setStep(4)}
                className="h-9 px-5 bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                {step === 2 ? 'Next →' : 'Continue →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 4: Income ── */}
      {step === 4 && (
        <div
          className="bg-background border border-border w-full sm:w-[480px] sm:max-w-[95vw] flex flex-col"
          style={{ minHeight: 400 }}
          onClick={e => e.stopPropagation()}
        >
          {/* Progress bar */}
          <ProgressBar step={step} />

          <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-b border-border">
            <span className="text-xs font-mono text-muted-foreground">Step 3 of 3</span>
            <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 flex flex-col px-6 py-8 sm:px-10 sm:py-10 gap-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Almost there.</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                What's your monthly income? This helps you see what's left to budget.
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Monthly income
              </label>
              <input
                type="number"
                min="0"
                placeholder="e.g. 3500"
                value={income}
                onChange={e => setIncome(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleFinish()}
                autoFocus
                className="h-11 w-full border border-border bg-background text-foreground px-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring font-mono [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4 border-t border-border">
            <button
              onClick={() => setStep(3)}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors min-w-[64px] px-3 py-2"
              >
                Skip for now
              </button>
              <button
                onClick={handleFinish}
                className="h-9 px-5 bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Get started →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnboardingModal;
